/**
 * Приоритеты, порядок и коды типов сущностей. §Хребет данных 3.1–3.2, §Ключ 6.1.
 *
 * Здесь ДВЕ независимые таблицы, и путать их нельзя:
 *   TYPE_PRIORITY    — сортировка; сверена с td/telegram/MessageEntity.cpp,
 *                      MessageEntity::get_type_priority (22.08.2026). Менять нельзя:
 *                      она решает, какая из двух сущностей одного диапазона станет внешней.
 *   ENTITY_TYPE_CODE — байтовая кодировка; к сортировке отношения не имеет
 *                      и никогда не перенумеровывается.
 */

import { CanonicalizeError } from '../issues.ts';

/** Дословно из TDLib. Значения не менять. */
const TYPE_PRIORITY: Readonly<Record<string, number>> = {
  blockquote: 0,
  expandable_blockquote: 0,
  pre: 11, // 10, если задан language — в TDLib это отдельный тип PreCode
  code: 20,
  date_time: 30, // FormattedDate; в MVP запрещён, в таблице для полноты
  text_link: 49,
  text_mention: 49,
  mention: 50,
  hashtag: 50,
  cashtag: 50,
  bot_command: 50,
  url: 50,
  email: 50,
  phone_number: 50,
  bank_card_number: 50,
  media_timestamp: 50,
  bold: 90,
  italic: 91,
  underline: 92,
  strikethrough: 93,
  spoiler: 94,
  custom_emoji: 99,
};

export function priorityOf(e: { type: string; language?: string | undefined }): number {
  if (e.type === 'pre') return e.language !== undefined ? 10 : 11;
  return TYPE_PRIORITY[e.type] ?? 99;
}

/** Коды для байтовой кодировки. Никогда не переиспользуются. */
export const ENTITY_TYPE_CODE = {
  bold: 1,
  italic: 2,
  underline: 3,
  strikethrough: 4,
  spoiler: 5,
  code: 6,
  pre: 7,
  text_link: 8,
  blockquote: 9,
  expandable_blockquote: 10,
} as const;

export type SendableEntityType = keyof typeof ENTITY_TYPE_CODE;

/** Ровно то, что мы имеем право отправить явно. */
export const SENDABLE_ENTITY_TYPES = Object.keys(ENTITY_TYPE_CODE) as readonly SendableEntityType[];

const SENDABLE_SET: ReadonlySet<string> = new Set(SENDABLE_ENTITY_TYPES);

export function isSendableEntityType(t: string): t is SendableEntityType {
  return SENDABLE_SET.has(t);
}

/**
 * Сервер проставляет их сам; отправить явно нельзя.
 * Вычитаются из ОБЕИХ карт атрибутов на шаге 4 §Ключ 6.2.
 */
export const AUTO_DETECTED_ENTITY_TYPES = [
  'url',
  'mention',
  'hashtag',
  'cashtag',
  'bot_command',
  'email',
  'phone_number',
] as const;

/**
 * Сервер вырезает их из JSON ответа (docs/telegram.md ПРАВИЛО 6.2).
 * НЕ вычитаются ниоткуда: их появление в ответе — сигнал, и ловит его шаг 7 §Ключ 6.2.
 */
export const STRIPPED_BY_SERVER_TYPES = ['bank_card_number', 'media_timestamp'] as const;

export type AutoDetectedEntityType = (typeof AUTO_DETECTED_ENTITY_TYPES)[number];
export type StrippedByServerType = (typeof STRIPPED_BY_SERVER_TYPES)[number];

const AUTO_SET: ReadonlySet<string> = new Set(AUTO_DETECTED_ENTITY_TYPES);

export function isAutoDetectedEntityType(t: string): t is AutoDetectedEntityType {
  return AUTO_SET.has(t);
}

interface SortableEntity {
  type: string;
  offset: number;
  length: number;
  language?: string | undefined;
}

/**
 * Единственная функция сортировки на весь репозиторий: её вызывает и кодировщик, и превью.
 * Ключи, в этом порядке (MessageEntity.h, operator<, проверено):
 *   offset ↑ → length ↓ → priorityOf ↑ → ENTITY_TYPE_CODE ↑
 *
 * Четвёртый ключ добавлен нами: в TDLib std::sort нестабилен, у нас порядок обязан быть
 * детерминированным. После канонизации полное совпадение всех четырёх ключей означает
 * дубликат сущности — это баг вызывающего, и он поднимается как entity_order_tie.
 */
export function sortEntities<E extends SortableEntity>(es: readonly E[]): E[] {
  const out = es.slice();
  out.sort((a, b) => {
    if (a.offset !== b.offset) return a.offset - b.offset;
    if (a.length !== b.length) return b.length - a.length;
    const pa = priorityOf(a);
    const pb = priorityOf(b);
    if (pa !== pb) return pa - pb;
    const ca = ENTITY_TYPE_CODE[a.type as SendableEntityType] ?? 0xff;
    const cb = ENTITY_TYPE_CODE[b.type as SendableEntityType] ?? 0xff;
    return ca - cb;
  });
  return out;
}

/**
 * Утверждение канонизации: после resplit двух неразличимых по всем четырём ключам
 * сущностей быть не может. Вызывается canonicalize() на своём выходе, а не sortEntities:
 * сортировка обязана оставаться тотальной, её зовут и на сыром вводе.
 */
export function assertNoOrderTie(sorted: readonly SortableEntity[]): void {
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1] as SortableEntity;
    const b = sorted[i] as SortableEntity;
    if (
      a.offset === b.offset &&
      a.length === b.length &&
      priorityOf(a) === priorityOf(b) &&
      a.type === b.type
    ) {
      throw new CanonicalizeError(
        'entity_order_tie',
        `two entities are indistinguishable by all four sort keys: ${a.type} at ${a.offset}+${a.length}`,
      );
    }
  }
}
